/**
 * Inventory Field Configuration
 * Defines which fields should be displayed to users vs excluded backend fields
 */

// Fields that should be displayed in the inventory table and edit modal
export const INVENTORY_DISPLAY_FIELDS = [
  "circuit_id",
  "provider",
  "category",
  "service_type",
  "status",
  "first_name",
  "last_name",
  "email",
  "bandwidth",
  "last_mile_provider",
  "provider_circuit_id",
  "phone",
  "contract_term",
  "end_of_term",
  "mrc",
  "billing_account",
  "works_with_circuit_id",
  "std_street_address",
  "std_city",
  "std_state",
  "std_zip",
  "address",
  "notes",
  "follow_up_date",
  "action",
];

// Backend/system fields that should be excluded from display
export const INVENTORY_EXCLUDED_FIELDS = [
  "id",
  "job_id",
  "tenant_id",
  "import_status",
  "address_id",
  "records_id",
  "created_at",
  "updated_at",
  "edited_columns",
  "record_status",
  "import_id",
  "import_record_id",
];

// Field label mappings for user-friendly display names
export const INVENTORY_FIELD_LABELS = {
  circuit_id: "Circuit ID",
  provider: "Provider",
  category: "Category",
  service_type: "Service Type",
  status: "Status",
  first_name: "First Name",
  last_name: "Last Name",
  email: "Email",
  bandwidth: "Bandwidth",
  last_mile_provider: "Last Mile Provider",
  provider_circuit_id: "Provider Circuit ID",
  phone: "Phone",
  contract_term: "Contract Term (months)",
  end_of_term: "End of Term",
  mrc: "MRC$",
  billing_account: "Billing Account",
  works_with_circuit_id: "Works with circuit id",
  std_street_address: "Address",
  std_city: "City",
  std_state: "State",
  std_zip: "ZIP",
  address: "Address",
  notes: "Notes",
  follow_up_date: "Follow Up Date",
  action: "Action",
  taxes_and_surcharges: "Taxes & Surcharges ($)",
  total: "Total ($)",
};

/**
 * Table Column Sequence Configuration for Inventory Table
 * Defines the exact order of columns to display in the inventory table
 * Simple array of field names in the desired sequence
 */
export const INVENTORY_TABLE_COLUMN_SEQUENCE = [
  "provider",
  "circuit_id",
  "service_type",
  "status",
  "address",
  "last_mile_provider",
  "provider_circuit_id",
  "bandwidth",
  "mrc",
  "contract_term",
  "end_of_term",
  "billing_account",
  "works_with_circuit_id",
  "phone",
  "first_name",
  "last_name",
  "email",
  "notes",
  "follow_up_date",
  "action",
];

/**
 * Grouped Table Column Configuration
 * Defines the exact columns to display in GroupedInventoryTable
 * Static configuration for consistent column display
 */
export const GROUPED_TABLE_COLUMNS = [
  "provider",
  "circuit_id",
  "service_type",
  "end_of_term",
  "billing_account",
  "mrc",
];

/**
 * Filter inventory record to only include display fields
 * @param {Object} record - Full inventory record from API
 * @returns {Object} - Filtered record with only display fields
 */
export const filterInventoryDisplayFields = (record) => {
  if (!record || typeof record !== "object") return record;

  const filtered = {};
  INVENTORY_DISPLAY_FIELDS.forEach((field) => {
    if (record.hasOwnProperty(field)) {
      filtered[field] = record[field];
    }
  });

  // Always include notes, follow_up_date, and action if they exist
  if (record.notes !== undefined) filtered.notes = record.notes;
  if (record.follow_up_date !== undefined)
    filtered.follow_up_date = record.follow_up_date;
  if (record.action !== undefined) filtered.action = record.action;

  // Always preserve id for API operations (needed for updates)
  if (record.id !== undefined) filtered.id = record.id;

  return filtered;
};

/**
 * Get field sequence for table columns from INVENTORY_TABLE_COLUMN_SEQUENCE configuration
 * Returns field names in the order defined in the configuration
 * Handles bandwidth field normalization
 * @returns {Array} - Array of field names in proper sequence
 */
export const getInventoryTableColumnSequence = () => {
  const sequence = [];
  const seenFields = new Set();

  // Process the configured sequence
  INVENTORY_TABLE_COLUMN_SEQUENCE.forEach((fieldName) => {
    const fieldLower = fieldName.toLowerCase();

    // Skip duplicates
    if (!seenFields.has(fieldName) && !seenFields.has(fieldLower)) {
      // Handle bandwidth - use "bandwidth" spelling
      if (fieldLower === "bandwidth") {
        if (!seenFields.has("bandwidth")) {
          sequence.push("bandwidth");
          seenFields.add("bandwidth");
        }
      } else {
        sequence.push(fieldName);
        seenFields.add(fieldName);
        seenFields.add(fieldLower);
      }
    }
  });

  return sequence;
};

/**
 * Get display columns from inventory records
 * Returns only fields that exist in the data and are in DISPLAY_FIELDS
 * Uses sequence from INVENTORY_TABLE_COLUMN_SEQUENCE for proper ordering
 * Filters out individual address columns when address field exists
 * @param {Array} records - Array of inventory records
 * @returns {Array} - Array of field names to display in proper sequence
 */
export const getInventoryDisplayColumns = (records) => {
  if (!Array.isArray(records) || records.length === 0) {
    return INVENTORY_DISPLAY_FIELDS;
  }

  const availableFields = new Set();
  const hasAddressField = records.some(
    (record) => record.address !== undefined && record.address !== null
  );
  const hasAddressComponents = records.some(
    (record) =>
      record.std_street_address ||
      record.std_city ||
      record.std_state ||
      record.std_zip ||
      record.std_country
  );

  records.forEach((record) => {
    Object.keys(record).forEach((key) => {
      const keyLower = key.toLowerCase();
      const isDisplayField = INVENTORY_DISPLAY_FIELDS.some(
        (field) => field.toLowerCase() === keyLower
      );
      const isExcluded = INVENTORY_EXCLUDED_FIELDS.some(
        (field) => field.toLowerCase() === keyLower
      );

      if (isDisplayField && !isExcluded) {
        availableFields.add(key);
      }
    });
  });

  // If address field exists or address components exist, filter out individual address columns
  const individualAddressFields = [
    "std_street_address",
    "std_city",
    "std_state",
    "std_zip",
    "std_country",
  ];

  // Get the proper sequence from configuration
  const fieldSequence = getInventoryTableColumnSequence();

  // Filter available fields and maintain sequence
  const filteredFields = [];

  // Add fields in sequence order if they're available
  fieldSequence.forEach((fieldName) => {
    const fieldLower = fieldName.toLowerCase();

    // Skip individual address fields if we have address components
    if (
      (hasAddressComponents || hasAddressField) &&
      individualAddressFields.includes(fieldName)
    ) {
      return;
    }

    // Check if field is available (case-insensitive)
    const matchingField = Array.from(availableFields).find(
      (key) => key.toLowerCase() === fieldLower
    );

    if (matchingField || fieldName === "address") {
      // For address, only add if we have address components
      if (fieldName === "address") {
        if (hasAddressComponents || hasAddressField) {
          if (!filteredFields.includes("address")) {
            filteredFields.push("address");
          }
        }
      } else {
        // Use the actual field name from availableFields (preserve original casing)
        const fieldToAdd = matchingField || fieldName;
        if (!filteredFields.includes(fieldToAdd)) {
          filteredFields.push(fieldToAdd);
        }
      }
    }
  });

  return filteredFields;
};

/**
 * Field Sequence Configuration for EditInventoryModal
 * Defines the order, grouping, and read-only status of fields
 */
export const INVENTORY_FIELD_SEQUENCE = [
  // Address Information Group (moved to top)
  {
    groupName: "Address Information",
    fields: [
      {
        name: "std_street_address",
        label: "Street Address",
        readOnly: false,
      },
      {
        name: "std_city",
        label: "City",
        readOnly: false,
      },
      {
        name: "std_state",
        label: "State",
        readOnly: false,
      },
      {
        name: "std_country",
        label: "Country",
        readOnly: false,
      },
      {
        name: "std_zip",
        label: "ZIP",
        readOnly: false,
      },
    ],
  },
  // Primary Fields Group (Read-only)
  {
    groupName: null, // No group header for primary fields
    fields: [
      {
        name: "provider",
        label: "Provider",
        readOnly: true,
      },
      {
        name: "category",
        label: "Category",
        readOnly: true,
      },
    ],
  },
  // General Fields Group
  {
    groupName: null,
    fields: [
      {
        name: "circuit_id",
        label: "Circuit ID",
        readOnly: false,
      },
      {
        name: "mrc",
        label: "MRC$",
        readOnly: false,
      },
      {
        name: "status",
        label: "Status",
        readOnly: false,
      },
      {
        name: "service_type",
        label: "Service Type",
        readOnly: false,
      },
      {
        name: "last_mile_provider",
        label: "Last Mile Provider",
        readOnly: false,
      },
      {
        name: "provider_circuit_id",
        label: "Provider Circuit ID",
        readOnly: false,
      },
      {
        name: "bandwidth",
        label: "Bandwidth",
        readOnly: false,
      },
      {
        label: "Bandwidth",
        readOnly: false,
      },
      {
        name: "contract_term",
        label: "Contract Term (months)",
        readOnly: false,
      },
      {
        name: "end_of_term",
        label: "End of Term",
        readOnly: false,
      },
      {
        name: "billing_account",
        label: "Billing Account",
        readOnly: false,
      },
      {
        name: "works_with_circuit_id",
        label: "Works with circuit id",
        readOnly: false,
      },
      {
        name: "phone",
        label: "Phone",
        readOnly: false,
      },
    ],
  },
  // Mobile Device Assigned To Group
  {
    groupName: "Mobile Device Assigned To",
    conditional: (formData) => {
      const category = formData.category || "";
      // Debug logging to verify category value
      console.log(
        "[Mobile Device Conditional] formData.category:",
        category,
        "Type:",
        typeof category
      );
      console.log("[Mobile Device Conditional] formData:", formData);
      const shouldShow = category === "Mobility"; // Exact case-sensitive match
      console.log("[Mobile Device Conditional] shouldShow:", shouldShow);
      return shouldShow;
    },
    fields: [
      {
        name: "first_name",
        label: "First Name",
        readOnly: false,
      },
      {
        name: "last_name",
        label: "Last Name",
        readOnly: false,
      },
      {
        name: "email",
        label: "Email",
        readOnly: false,
      },
    ],
  },
  // Local Site Contact Group
  {
    groupName: "Local Site Contact",
    fields: [
      {
        name: "primary_contact_name",
        label: "Primary Contact Name",
        readOnly: false,
      },
      {
        name: "primary_contact_email",
        label: "Primary Contact Email",
        readOnly: false,
      },
      {
        name: "primary_contact_phone",
        label: "Primary Contact Phone",
        readOnly: false,
      },
      {
        name: "secondary_contact_name",
        label: "Secondary Contact Name",
        readOnly: false,
      },
      {
        name: "secondary_contact_email",
        label: "Secondary Contact Email",
        readOnly: false,
      },
      {
        name: "secondary_contact_phone",
        label: "Secondary Contact Phone",
        readOnly: false,
      },
    ],
  },
  // Notes and Follow-up Date (no group header)
  {
    groupName: null,
    fields: [
      {
        name: "notes",
        label: "Notes",
        readOnly: false,
      },
      {
        name: "follow_up_date",
        label: "Follow Up Date",
        readOnly: false,
      },
      {
        name: "action",
        label: "Action",
        readOnly: false,
      },
    ],
  },
];
